/**
 * OIMS v2 - Actions tab (Command Bridge)
 * Execute SSM commands with live streaming output
 */

import { escapeHtml, clearLog, appendLog } from './utils.js';
import { showApprovalRequested } from './approvals.js';

// Module-level DOM refs
let selAction, selInstance, btnExecute, logArea;
let sse = null;
let sseEnded = false;
let sseRetryCount = 0;
let currentExecMeta = null;

// Recent operations history (session-only, max 10)
const recentOps = [];
const MAX_RECENT_OPS = 10;

// Quick action icon map
const ACTION_ICONS = {
  restart_birt:       'bi-file-earmark-bar-graph',
  restart_wildfly:    'bi-fire',
  restart_jbpm:       'bi-diagram-3',
  restart_docker:     'bi-box-seam',
  restart_iis_apppool:'bi-window',
  restart_dep:        'bi-arrow-repeat',
  restart_fe:         'bi-layout-text-window',
  restart_be:         'bi-server',
  backup_sql:         'bi-database-fill-up',
  restart_sql:        'bi-database-gear',
  reboot_instance:    'bi-power',
};

export function initActions(state) {
  selAction = document.getElementById("selAction");
  selInstance = document.getElementById("selInstance");
  btnExecute = document.getElementById("btnExecute");
  logArea = document.getElementById("logArea");

  if (!selAction || !selInstance) return;

  // On action change, reload instances + update quick action highlight + briefing
  selAction.addEventListener("change", () => {
    loadInstances(state, selAction.value);
    highlightQuickAction(selAction.value);
    updateBriefing();
  });

  // On instance change, update briefing
  selInstance.addEventListener("change", updateBriefing);

  // Execute button -> confirm modal
  btnExecute && btnExecute.addEventListener("click", () => confirmAndExecute(state));

  // Terminal toolbar
  const btnClear = document.getElementById("btnClearLog");
  const btnCopy = document.getElementById("btnCopyLog");
  if (btnClear) btnClear.addEventListener("click", () => clearLog(logArea));
  if (btnCopy) btnCopy.addEventListener("click", () => copyLogToClipboard());

  // Dashboard navigation: pre-select action from Quick Actions card
  document.addEventListener('oims:navigate-action', (e) => {
    const { actionId } = e.detail || {};
    if (!actionId || !selAction) return;
    // Wait a tick for tab switch to complete
    setTimeout(() => {
      if (selAction.querySelector(`option[value="${CSS.escape(actionId)}"]`)) {
        selAction.value = actionId;
        selAction.dispatchEvent(new Event('change'));
      }
    }, 100);
  });

  // Start streaming after approval/break-glass execution (non-IIS actions)
  document.addEventListener('oims:approval-executed', (e) => {
    const { command_id, instance_id, instance_name, action_id, action_label, request_id } = e.detail || {};
    if (!command_id || !instance_id) return;
    // IIS actions are handled by windows.js
    if (action_id === 'restart_iis_apppool') return;
    const display_name = instance_name || instance_id;
    const actionLabel = action_label || action_id;
    const displayName = display_name;
    currentExecMeta = { instance_id, command_id, action_id, request_id, display_name, actionLabel, displayName };
    if (logArea) {
      clearLog(logArea);
      appendLog(logArea, `[Approved] Streaming output for ${display_name}...`);
    }
    setLaunchState(true);
    startStream(state);
  });
}

function setStatus(status, color = "secondary") {
  const badge = document.getElementById("statusBadge");
  if (badge) {
    badge.textContent = status;
    const variantMap = { secondary: "idle", info: "running", success: "success", danger: "error", primary: "running", warning: "running" };
    const variant = variantMap[color] || "idle";
    badge.className = `status-badge ${variant}`;
  }
}

export async function loadActions(state) {
  if (!state.accessToken) return;
  try {
    const { data } = await axios.get("/actions/available");
    selAction.innerHTML = "";
    data.actions.forEach(a => {
      const opt = document.createElement("option");
      opt.value = a.action;
      opt.textContent = a.label;
      opt.dataset.command = a.command;
      if (a.requires_approval) opt.dataset.requiresApproval = "1";
      selAction.appendChild(opt);
    });
    // Build quick action cards from available actions
    buildQuickActions(data.actions, state);
  } catch (e) {
    console.error("loadActions error", e);
  }
}

function buildQuickActions(actions, state) {
  const grid = document.getElementById("quickActionsGrid");
  if (!grid) return;
  grid.innerHTML = "";

  actions.forEach(a => {
    const btn = document.createElement("button");
    btn.className = "quick-action-btn";
    btn.dataset.action = a.action;
    btn.title = a.label;

    const iconClass = ACTION_ICONS[a.action] || 'bi-play-circle';
    btn.innerHTML = `<i class="bi ${iconClass}"></i><span>${escapeHtml(a.label)}</span>`;

    btn.addEventListener("click", () => {
      // Select this action in the dropdown
      selAction.value = a.action;
      selAction.dispatchEvent(new Event("change"));
      highlightQuickAction(a.action);
    });

    grid.appendChild(btn);
  });
}

function highlightQuickAction(actionId) {
  const grid = document.getElementById("quickActionsGrid");
  if (!grid) return;
  grid.querySelectorAll(".quick-action-btn").forEach(btn => {
    btn.classList.toggle("active", btn.dataset.action === actionId);
  });
}

function updateBriefing() {
  const briefingText = document.getElementById("briefingText");
  if (!briefingText) return;

  const actionLabel = selAction?.options[selAction.selectedIndex]?.textContent?.trim();
  const instanceLabel = selInstance?.options[selInstance.selectedIndex]?.textContent?.trim();
  const hasAction = selAction?.value;
  const hasInstance = selInstance?.value;

  if (hasAction && hasInstance) {
    briefingText.innerHTML = `<span class="briefing-ready"><i class="bi bi-check-circle me-1"></i>Ready: <strong>${escapeHtml(actionLabel)}</strong> on <strong>${escapeHtml(instanceLabel)}</strong></span>`;
  } else if (hasAction) {
    briefingText.textContent = `Action: ${actionLabel} — select a target instance`;
  } else {
    briefingText.textContent = "Select an action and instance to begin";
  }

  // Update launch button subtitle
  const launchSmall = btnExecute?.querySelector(".launch-text small");
  if (launchSmall) {
    if (hasAction && hasInstance) {
      launchSmall.textContent = `${actionLabel} → ${instanceLabel}`;
    } else {
      launchSmall.textContent = "Select action & target first";
    }
  }
}

function copyLogToClipboard() {
  if (!logArea) return;
  const text = logArea.innerText || logArea.textContent || "";
  if (!text.trim()) {
    window.toast?.info("Nothing to copy.");
    return;
  }
  navigator.clipboard.writeText(text).then(() => {
    window.toast?.success("Output copied to clipboard.");
  }).catch(() => {
    window.toast?.error("Failed to copy.");
  });
}

export async function loadInstances(state, actionId = null) {
  if (!state.accessToken) return;
  try {
    const params = {};
    if (actionId) params.action_id = actionId;

    selInstance.innerHTML = "";
    const loadingOpt = document.createElement("option");
    loadingOpt.textContent = "Loading instances...";
    loadingOpt.disabled = true;
    selInstance.appendChild(loadingOpt);

    const { data } = await axios.get("/actions/instances", { params });
    selInstance.innerHTML = "";
    data.instances.forEach(inst => {
      const opt = document.createElement("option");
      opt.value = inst.instance_id;
      const displayName = inst.display_name || inst.name;
      opt.textContent = displayName;
      opt.dataset.group = inst.group;
      opt.dataset.displayName = displayName;
      if (inst.container) {
        opt.dataset.container = inst.container;
      }
      selInstance.appendChild(opt);
    });
    updateBriefing();
  } catch (e) {
    console.error("loadInstances error", e);
  }
}

function confirmAndExecute(state) {
  if (!state.accessToken) {
    window.toast.warning("Please sign in to execute actions.");
    return;
  }
  const action_id = selAction.value;
  const instance_id = selInstance.value;
  if (!action_id || !instance_id) {
    window.toast.warning("Please select both an action and instance.");
    return;
  }

  const selectedOption = selInstance.options[selInstance.selectedIndex];
  const instanceName = selectedOption?.textContent?.trim() || instance_id;
  const actionLabel = selAction.options[selAction.selectedIndex]?.textContent?.trim() || action_id;

  document.getElementById("confirmActionName").textContent = actionLabel;
  document.getElementById("confirmInstanceName").textContent = instanceName;

  // Show/hide approval hint in confirm modal
  const approvalHint = document.getElementById("confirmApprovalHint");
  const needsApproval = selectedOption?.closest('select')?.previousElementSibling
    ? false
    : selAction.options[selAction.selectedIndex]?.dataset?.requiresApproval === "1";
  if (approvalHint) {
    approvalHint.style.display = needsApproval ? '' : 'none';
  }

  const modalEl = document.getElementById("confirmActionModal");
  const modal = new bootstrap.Modal(modalEl);
  modal.show();

  const btnConfirm = document.getElementById("btnConfirmExecute");
  const newBtn = btnConfirm.cloneNode(true);
  btnConfirm.parentNode.replaceChild(newBtn, btnConfirm);
  newBtn.addEventListener("click", () => {
    modal.hide();
    executeAction(state);
  });
}

function showExecMetadata(actionLabel, targetName) {
  const meta = document.getElementById("execMetadata");
  const metaAction = document.getElementById("metaAction");
  const metaTarget = document.getElementById("metaTarget");
  const metaTime = document.getElementById("metaTime");
  if (meta) {
    meta.style.display = "";
    if (metaAction) metaAction.textContent = actionLabel;
    if (metaTarget) metaTarget.textContent = targetName;
    if (metaTime) metaTime.textContent = new Date().toLocaleTimeString();
  }
}

function hideExecMetadata() {
  const meta = document.getElementById("execMetadata");
  if (meta) meta.style.display = "none";
}

function setLaunchState(executing) {
  if (!btnExecute) return;
  if (executing) {
    btnExecute.disabled = true;
    btnExecute.classList.add("executing");
    const icon = btnExecute.querySelector(".launch-icon i");
    if (icon) { icon.className = "bi bi-arrow-repeat"; }
    const strong = btnExecute.querySelector(".launch-text strong");
    if (strong) strong.textContent = "Executing...";
  } else {
    btnExecute.disabled = false;
    btnExecute.classList.remove("executing");
    const icon = btnExecute.querySelector(".launch-icon i");
    if (icon) { icon.className = "bi bi-rocket-takeoff-fill"; }
    const strong = btnExecute.querySelector(".launch-text strong");
    if (strong) strong.textContent = "Execute Command";
    updateBriefing();
  }
}

function addRecentOp(actionLabel, targetName, status) {
  recentOps.unshift({
    action: actionLabel,
    target: targetName,
    status,
    time: new Date(),
  });
  if (recentOps.length > MAX_RECENT_OPS) recentOps.pop();
  renderRecentOps();
}

function renderRecentOps() {
  const wrap = document.getElementById("recentOps");
  const list = document.getElementById("recentOpsList");
  if (!wrap || !list) return;

  if (recentOps.length === 0) {
    wrap.style.display = "none";
    return;
  }

  wrap.style.display = "";
  list.innerHTML = "";

  recentOps.forEach(op => {
    const statusIcon = op.status === "success" ? "bi-check-circle-fill" : op.status === "error" ? "bi-x-circle-fill" : op.status === "pending" ? "bi-shield-lock-fill" : "bi-arrow-repeat";
    const statusClass = op.status === "success" ? "op-success" : op.status === "error" ? "op-error" : op.status === "pending" ? "op-pending" : "op-running";
    const timeStr = op.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const item = document.createElement("div");
    item.className = "recent-op-item";
    item.innerHTML = `
      <div class="recent-op-left">
        <span class="recent-op-icon ${statusClass}"><i class="bi ${statusIcon}"></i></span>
        <span class="recent-op-name">${escapeHtml(op.action)}</span>
        <span class="recent-op-target">${escapeHtml(op.target)}</span>
      </div>
      <span class="recent-op-time">${timeStr}</span>`;
    list.appendChild(item);
  });
}

async function executeAction(state) {
  const action_id = selAction.value;
  const instance_id = selInstance.value;
  const selectedOption = selInstance.options[selInstance.selectedIndex];
  const container = selectedOption?.dataset?.container;
  const instanceName = selectedOption?.textContent?.trim() || instance_id;
  const actionLabel = selAction.options[selAction.selectedIndex]?.textContent?.trim() || action_id;

  setLaunchState(true);

  if (sse) stopStream();
  clearLog(logArea);
  setStatus("Starting...", "secondary");
  const displayName = container || instanceName;
  showExecMetadata(actionLabel, displayName);
  appendLog(logArea, `Executing: ${actionLabel} on ${displayName}...`);

  try {
    let url = `/actions/execute?action_id=${encodeURIComponent(action_id)}&instance_id=${encodeURIComponent(instance_id)}`;
    if (container) {
      url += `&container=${encodeURIComponent(container)}`;
    }
    const { data } = await axios.post(url);

    // Maker/Checker: action requires approval
    if (data.status === "approval_required") {
      setStatus("Approval Pending", "warning");
      appendLog(logArea, `⏳ ${data.message || 'Approval required for this action.'}`);
      setLaunchState(false);
      addRecentOp(actionLabel, displayName, "pending");
      showApprovalRequested(data);
      return;
    }

    if (data.status !== "sent") {
      setStatus("Error", "danger");
      appendLog(logArea, "ERROR: " + (data.message || "unknown"));
      setLaunchState(false);
      addRecentOp(actionLabel, displayName, "error");
      return;
    }

    const notifName = container || selectedOption?.dataset?.displayName || instanceName;
    currentExecMeta = { instance_id, command_id: data.command_id, action_id, display_name: notifName, actionLabel, displayName };
    appendLog(logArea, `Command sent to ${displayName}. Waiting for output...`);
    startStream(state);
  } catch (e) {
    setStatus("Error", "danger");
    appendLog(logArea, "Execution error: " + e.message);
    setLaunchState(false);
    addRecentOp(actionLabel, displayName, "error");
  }
}

function startStream(state) {
  if (!currentExecMeta || !state.accessToken) {
    window.toast.warning("No command to stream.");
    return;
  }
  if (sse && sse.readyState !== EventSource.CLOSED) {
    appendLog(logArea, "[SSE] Stream already running; ignoring second start.");
    return;
  }

  sseEnded = false;

  const { instance_id, command_id, action_id, display_name, request_id } = currentExecMeta;
  const url = new URL("/actions/stream", window.location.origin);
  url.searchParams.set("instance_id", instance_id);
  url.searchParams.set("command_id", command_id);
  url.searchParams.set("access_token", state.accessToken);
  if (action_id) url.searchParams.set("action_id", action_id);
  if (display_name) url.searchParams.set("display_name", display_name);
  if (request_id) url.searchParams.set("request_id", request_id);

  sse = new EventSource(url.toString());
  appendLog(logArea, "[SSE] Opened stream...");
  setStatus("Streaming", "primary");

  sse.onopen = () => {
    sseRetryCount = 0;
    setStatus("Streaming", "primary");
  };

  sse.addEventListener("message", (ev) => {
    try {
      const payload = JSON.parse(ev.data);
      if (payload && payload.chunk) {
        appendLog(logArea, payload.chunk);
      } else {
        appendLog(logArea, ev.data);
      }
    } catch {
      appendLog(logArea, ev.data);
    }
  });

  sse.addEventListener("done", () => {
    sseEnded = true;
    setStatus("Finished", "success");
    appendLog(logArea, "\n────────────────────────────────────");
    appendLog(logArea, "[SSE] Command finished.");
    setLaunchState(false);
    if (currentExecMeta) {
      addRecentOp(currentExecMeta.actionLabel || "Command", currentExecMeta.displayName || "Instance", "success");
    }
    setTimeout(stopStream, 120);
  });

  sse.addEventListener("error", () => {
    if (sseEnded || !sse) return;
    if (sse.readyState === EventSource.CLOSED) {
      stopStream();
      setStatus("Closed", "dark");
      setLaunchState(false);
      if (currentExecMeta) {
        addRecentOp(currentExecMeta.actionLabel || "Command", currentExecMeta.displayName || "Instance", "error");
      }
      sseRetryCount = 0;
      return;
    }
    stopStream();
    sseRetryCount++;
    if (sseRetryCount <= 3 && currentExecMeta) {
      const delay = Math.min(1000 * Math.pow(2, sseRetryCount - 1), 8000);
      setStatus("Reconnecting...", "warning");
      appendLog(logArea, `[SSE] Connection lost. Retrying in ${delay / 1000}s (attempt ${sseRetryCount}/3)...`);
      setTimeout(() => startStream(state), delay);
    } else {
      setStatus("Error", "danger");
      appendLog(logArea, "[SSE] Connection failed after retries.");
      setLaunchState(false);
      if (currentExecMeta) {
        addRecentOp(currentExecMeta.actionLabel || "Command", currentExecMeta.displayName || "Instance", "error");
      }
      sseRetryCount = 0;
    }
  });
}

function stopStream() {
  try {
    if (sse) {
      sse.close();
      sse = null;
      appendLog(logArea, "[SSE] Closed stream.");
    }
  } finally {
    if (!sseEnded) {
      setStatus("Stopped", "warning");
    }
  }
}

export { confirmAndExecute };
